import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import vasstrOrangeLogo from '../media/vasstrOrangeLogo.png';
import Base from './Base';
import { CheckCircle2, Info, Sparkles } from 'lucide-react';
import { Card, Button, Badge, Alert, Spinner } from './ui';
import API_BASE_URL from '../config/api';

const subscriptionPlans = [
  {
    months: 0, price: 0, originalPrice: 0, taxes: 0, name: "Free Trial", isFree: true,
    features: [
      "🎉 *1 Month FREE Trial* - No payment required!",
      "🛍 Branded designer storefront",
      "🎨 Upload unlimited designs*",
      "🧾 Order management, invoices & live tracking",
      "📦 Online Payments & Paymentgateway support via Vasstr",
      "🧵 Access to 'Place a Bid' feature",
      "💰 payment is released right after order* is confirmed",
    ],
  },
  {
    months: 3, price: 2857, originalPrice: 13500, taxes: (0.18 * 2857).toFixed(2), name: "Growth",
    features: [
      "🛍 Branded designer storefront",
      "🎨 Upload unlimited designs*",
      "🧾 Order management, invoices & live tracking",
      "📦 Online Payments & Paymentgateway support via Vasstr",
      "🧵 Access to 'Place a Bid' feature",
      "💰 payment is released right after order* is confirmed",
    ],
  },
  {
    months: 6, price: 5389, originalPrice: 27000  , taxes: (0.18 * 5389).toFixed(2), name: "Pro",
    features: [
      "🛍 Branded designer storefront",
      "🎨 Upload unlimited designs*",
      "🧾 Order management, invoices & live tracking",
      "📦 Online Payments & Paymentgateway support via Vasstr",
      "🧵 Access to 'Place a Bid' feature",
      "💰 payment is released right after order* is confirmed",
    ],
  },
  {
    months: 12, price: 9687, originalPrice: 54000, taxes: (0.18 * 9687).toFixed(2), name: "Enterprise", isPopular: true,
    features: [
      "🛍 Branded designer storefront",
      "🎨 Upload unlimited designs*",
      "🧾 Order management, invoices & live tracking",
      "📦 Online Payments & Paymentgateway support via Vasstr",
      "🧵 Access to 'Place a Bid' feature",
      "💰 payment is released right after order* is confirmed",
    ],
  },
];

const Subscription = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
    amount: 0.0,
    plan: null,
    is_cancelled: false,
    has_used_free_trial: false,
  });
  const { authToken } = useContext(AuthContext);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchSubscriptionStatus = async () => {
    if (!authToken) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/get_subscription_status/`,
        {
          headers: {
            Authorization: `Bearer ${authToken.access}`,
            'Content-Type': 'application/json',
          },
        }
      );
      console.log(response.data)
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      Swal.fire('Error!', 'Failed to fetch subscription status. Please try refreshing.', 'error');
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchSubscriptionStatus();
    }
  }, [authToken]);

  const handleFreeTrialActivation = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/activate_free_trial/`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken.access}`,
            'Content-Type': 'application/json',
          },
        }
      );

      Swal.fire({
        title: 'Success!',
        html: `
          <p>Your 1-month FREE trial has been activated!</p>
          <p>You can now fully utilize your VASSTR dashboard.</p>
          <p class="mt-3"><b class="font-bold text-[#d45614]">Don't forget to keep relevant outfits in stock!</b></p>
          <a href="/homepage" class="inline-block mt-4 bg-[#d45614] text-white font-semibold px-6 py-2 rounded-lg hover:bg-[#a8430e] transition duration-150">
            Go to Dashboard
          </a>
        `,
        icon: 'success',
        showConfirmButton: false,
        showCloseButton: true,
        customClass: {
          popup: 'font-sans'
        }
      });

      await fetchSubscriptionStatus();
      setSelectedPlan(null);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Failed to activate free trial. Please try again.', 'error');
    }
  };

  const handleSubscriptionSubmit = async () => {
    if (selectedPlan === null) {
      Swal.fire('Error', 'Please select a subscription plan first.', 'error');
      return;
    }

    // Handle free trial separately
    if (selectedPlan === 0) {
      await handleFreeTrialActivation();
      return;
    }

    try {
      console.log(selectedPlan)
      const response = await axios.post(
        `${API_BASE_URL}/api/save_subscription/`,
        { plan: selectedPlan },
        {
          headers: {
            Authorization: `Bearer ${authToken.access}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const { razorpay_order_id, razorpay_key, amount, plan } = response.data;

      const options = {
        key: razorpay_key,
        amount: amount * 100,
        currency: 'INR',
        name: 'VASSTR',
        image: vasstrOrangeLogo,
        description: `Subscription Plan: ${plan} Month${plan > 1 ? 's' : ''}`,
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${API_BASE_URL}/api/verify_payment/`,
              {
                plan: selectedPlan,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              },
              {
                headers: {
                  Authorization: `Bearer ${authToken.access}`,
                  'Content-Type': 'application/json',
                },
              }
            );

            Swal.fire({
              title: 'Success!',
              html: `
                <p>Payment verified and subscription activated.</p>
                <p>You can now fully utilize your VASSTR dashboard.</p>
                <p class="mt-3"><b class="font-bold text-[#d45614]">Don't forget to keep relevant outfits in stock!</b></p>
                <a href="/homepage" class="inline-block mt-4 bg-[#d45614] text-white font-semibold px-6 py-2 rounded-lg hover:bg-[#a8430e] transition duration-150">
                  Go to Dashboard
                </a>
              `,
              icon: 'success',
              showConfirmButton: false,
              showCloseButton: true,
              customClass: {
                popup: 'font-sans'
              }
            });

            await fetchSubscriptionStatus();
            setSelectedPlan(null);
          } catch (err) {
            Swal.fire('Error', 'Payment verification failed. Please contact support if debited.', 'error');
          }
        },
        theme: { color: '#F97316' },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        Swal.fire('Payment Failed', `Error: ${response.error.description}. Reason: ${response.error.reason}. Please try again.`, 'error');
      });
      rzp.open();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'Failed to initiate payment. Please try again.', 'error');
    }
  };

  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    date.setHours(23, 59, 0, 0);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    }) + " at 11:59 PM IST";
  };

  return (
    <Base Content={
      <div className="p-3">
        <div className="max-w-7xl mx-auto">
          <section id="subscription-plan">
            <h2 className="text-4xl font-extrabold tracking-tight text-center text-slate-800 mb-3">
              Choose Your Subscription Plan
            </h2>
            <p className="text-center text-slate-600 mb-10 text-lg">Unlock powerful features to boost your sales.</p>

            {subscriptionStatus.is_active && (
              <div className="bg-emerald-50 border-l-4 border-emerald-500 text-emerald-700 p-4 rounded-md shadow-sm mb-10 max-w-3xl mx-auto" role="alert">
                <div className="flex items-start">
                  <Info className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-emerald-600" />
                  <div>
                    <p className="font-bold text-emerald-800">Your Current Subscription</p>
                    <p className="text-sm"><b>Plan:</b> {subscriptionStatus.plan > -1 ? `${subscriptionStatus.plan} Month${subscriptionStatus.plan > 1 ? 's' : ''}` : 'N/A'}</p>
                    <p className="text-sm"><b>Expires on:</b> {formatExpiryDate(subscriptionStatus.expiry_date)}</p>
                    {subscriptionStatus.is_cancelled && (
                      <p className="text-sm text-red-600 font-semibold mt-1">Your subscription has been cancelled and will not renew upon expiry.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto my-8">
              {subscriptionPlans.map((plan) => (
                <div
                  key={plan.months}
                  className={`
                    !font-lora relative bg-white border p-6 rounded-2xl transition-all duration-300 shadow-lg flex flex-col
                    ${selectedPlan === plan.months ?
                      'border-orange-500 ring-2 ring-orange-500 scale-[1.03] shadow-orange-200' :
                      'border-gray-200 hover:border-orange-400 hover:shadow-xl'
                    }
                    ${subscriptionStatus.is_active ?
                      'opacity-60 cursor-not-allowed grayscale-[50%]' :
                      'hover:-translate-y-1.5'
                    }
                  `}
                  onClick={() => !subscriptionStatus.is_active && setSelectedPlan(plan.months)}
                >
                  {plan.isPopular && (
                    <div className="!font-lora absolute -top-3.5 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center z-10">
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> POPULAR
                    </div>
                  )}

                  <h3 className="!font-lora text-2xl font-bold text-slate-800 text-center mb-1 tracking-tight pt-3">{plan.name}</h3>
                  <p className="!font-lora text-sm text-gray-500 text-center mb-4">
                    {plan.isFree ? '1 Month FREE' : `${plan.months} Month${plan.months > 1 ? 's' : ''} Plan`}
                  </p>

                  <div className="my-4 text-center">
                    {plan.isFree ? (
                      <>
                        <span className="!font-lora text-4xl font-extrabold text-green-600">FREE</span>
                        <p className="!font-lora text-xs text-gray-500 mt-1">
                          No credit card required
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="!font-lora text-4xl font-extrabold text-[#d45614]">₹{plan.price}</span>
                        <span className="!font-lora text-lg text-gray-500 line-through ml-2">₹{plan.originalPrice}</span>
                        <p className="!font-lora text-xs text-gray-500 mt-1">
                          per {plan.months} month{plan.months > 1 ? 's' : ''} <span className="font-medium">(billed as one payment)</span>
                        </p>
                      </>
                    )}
                  </div>

                  <div className="text-center mb-6">
                    {plan.isFree ? (
                      <span className="!font-lora bg-green-100 text-green-800 text-sm font-semibold px-3 py-1.5 rounded-full">
                        🎉 Try Risk-Free
                      </span>
                    ) : (
                      <span className="!font-lora bg-orange-100 text-orange-800 text-sm font-semibold px-3 py-1.5 rounded-full">
                        Save {Math.round((1 - plan.price / plan.originalPrice) * 100)}%
                      </span>
                    )}
                  </div>

                  <ul className="space-y-2.5 mb-6 text-sm text-gray-700 flex-grow min-h-[100px]">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start !font-lora ">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2.5 mt-0.5 flex-shrink-0" />
                        <span className="!font-lora leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <p className="!font-lora text-xs text-gray-500 text-center mt-auto pt-4 border-t border-gray-100">
                    {plan.isFree
                      ? (subscriptionStatus.has_used_free_trial
                          ? '⚠️ Free trial already used'
                          : '✨ Start your journey today!')
                      : `+ ₹${plan.taxes} GST. Billed once for ${plan.months} month${plan.months > 1 ? 's' : ''}.`
                    }
                  </p>

                  {selectedPlan === plan.months && !subscriptionStatus.is_active && (
                    <div className="!font-lora absolute top-3 right-3 bg-green-500 text-white rounded-full p-1.5 shadow-md">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <button
                onClick={handleSubscriptionSubmit}
                className={`font-semibold px-10 py-4 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-150 text-xl
                  ${subscriptionStatus.is_active
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                    : selectedPlan === 0
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                    : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
                }`}
                disabled={subscriptionStatus.is_active || (selectedPlan === 0 && subscriptionStatus.has_used_free_trial)}
              >
                {subscriptionStatus.is_active
                  ? 'Subscription Active'
                  : selectedPlan === 0
                  ? (subscriptionStatus.has_used_free_trial ? 'Free Trial Already Used' : 'Start Free Trial')
                  : 'Subscribe Now'}
              </button>
            </div>
          </section>
        </div>
      </div>
    }/>
  );
};

export default Subscription;